import React from "react";
import { ArrowRight, CheckCircle2, Mail, MapPin, Phone } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { caseStudies, companyInfo } from "@/mock";

function CaseStudyDetail() {
  const { caseSlug } = useParams();
  const study = caseStudies.find((s) => s.slug === caseSlug);

  if (!study) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Pagina non trovata</h1>
          <Link to="/">
            <Button className="bg-orange-600 hover:bg-orange-700 text-white">Torna alla Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const gallery = (study.galleryImages?.length ? study.galleryImages : [study.image]).filter(Boolean);

  return (
    <div className="min-h-screen bg-white">
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-md shadow-sm z-50 transition-all duration-300">
        <nav className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3">
            <img
              src="https://customer-assets.emergentagent.com/job_renewable-energy-hub-19/artifacts/wxu8fra2_logo-MTSenergia-arancio-1.png.webp"
              alt="MTS Energia Logo"
              className="h-14 w-auto"
            />
          </Link>
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/#about" className="text-gray-700 hover:text-orange-600 transition-colors font-medium">
              Chi Siamo
            </Link>
            <Link to="/#divisions" className="text-gray-700 hover:text-orange-600 transition-colors font-medium">
              Servizi
            </Link>
            <Link to="/#projects" className="text-gray-700 hover:text-orange-600 transition-colors font-medium">
              Portfolio
            </Link>
            <Link to="/codice-etico" className="text-gray-700 hover:text-orange-600 transition-colors font-medium">
              Codice Etico
            </Link>
            <Link to="/#contact" className="text-gray-700 hover:text-orange-600 transition-colors font-medium">
              Contatti
            </Link>
            <Link to="/#contact">
              <Button className="bg-orange-600 hover:bg-orange-700 text-white px-6 transition-all duration-300 hover:scale-105">
                Richiedi Preventivo
              </Button>
            </Link>
          </div>
        </nav>
      </header>

      <section className="pt-32 pb-10 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto text-center">
            <Badge className="mb-4 bg-green-100 text-green-700 px-4 py-2">RISPARMIO ENERGETICO: LAVORI ESEGUITI</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900">{study.title}</h1>
            <p className="text-gray-600 mt-3 flex items-center justify-center gap-2">
              <MapPin className="w-4 h-4" />
              {study.location}
            </p>
          </div>
        </div>
      </section>

      <section className="pb-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-10 items-start">
              <div className="hidden lg:grid grid-cols-2 gap-4">
                {gallery.slice(0, 5).map((src, idx) => (
                  <div
                    key={idx}
                    className={`overflow-hidden rounded-2xl border border-gray-100 shadow-lg ${idx === 0 ? "col-span-2" : ""}`}
                  >
                    <img
                      src={src}
                      alt={`${study.title} ${idx + 1}`}
                      className={`w-full object-cover ${idx === 0 ? "h-64" : "h-52"}`}
                    />
                  </div>
                ))}
              </div>

              <div className="lg:hidden">
                <Carousel className="w-full">
                  <CarouselContent>
                    {gallery.map((src, idx) => (
                      <CarouselItem key={idx}>
                        <div className="p-1">
                          <div className="overflow-hidden rounded-2xl border border-gray-100 shadow-lg">
                            <img src={src} alt={`${study.title} ${idx + 1}`} className="w-full h-[320px] object-cover" />
                          </div>
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious />
                  <CarouselNext />
                </Carousel>
              </div>

              <Card className="border border-gray-100 shadow-lg rounded-2xl">
                <CardContent className="p-8">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-xl">
                      <p className="text-sm text-gray-600 mb-1">Istituto</p>
                      <p className="font-semibold text-gray-900">{study.type}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-xl">
                      <p className="text-sm text-gray-600 mb-1">Installazione</p>
                      <p className="font-semibold text-gray-900">{study.installation}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-xl">
                      <p className="text-sm text-gray-600 mb-1">Potenza installata</p>
                      <p className="font-semibold text-gray-900">{study.power}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-xl">
                      <p className="text-sm text-gray-600 mb-1">Risparmio</p>
                      <p className="font-semibold text-green-700">{study.savings}%</p>
                    </div>
                  </div>

                  <div className="mt-6 space-y-3 text-gray-700">
                    <div className="flex gap-2">
                      <span className="font-semibold min-w-[92px]">Moduli:</span>
                      <span>{study.modules}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="font-semibold min-w-[92px]">Inverter:</span>
                      <span>{study.inverter}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="font-semibold min-w-[92px]">Pompe:</span>
                      <span>{study.heatPump}</span>
                    </div>
                  </div>

                  <div className="mt-8">
                    <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      Interventi Realizzati
                    </h2>
                    <div className="mt-4 space-y-2">
                      {study.interventions.map((i, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-gray-700 text-sm">
                          <div className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                          {i}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3 justify-center">
                    <Link to="/#projects" className="w-full sm:w-auto">
                      <Button
                        variant="outline"
                        className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-5 py-4 text-base font-semibold w-full sm:w-[220px]"
                      >
                        Torna al Portfolio <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                    </Link>
                    <Link to="/#contact" className="w-full sm:w-auto">
                      <Button className="bg-orange-600 hover:bg-orange-700 text-white px-5 py-4 text-base font-semibold w-full sm:w-[220px]">
                        Inizia il Tuo Progetto <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-gray-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img
                  src="https://customer-assets.emergentagent.com/job_renewable-energy-hub-19/artifacts/wxu8fra2_logo-MTSenergia-arancio-1.png.webp"
                  alt="MTS Energia Logo"
                  className="h-12 w-auto"
                />
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">
                Professionisti delle rinnovabili dal {companyInfo.foundingYear}
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-lg">Servizi</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>
                  <Link to="/#divisions" className="hover:text-orange-400 transition-colors">
                    Operation & Maintenance
                  </Link>
                </li>
                <li>
                  <Link to="/#divisions" className="hover:text-orange-400 transition-colors">
                    Consulting
                  </Link>
                </li>
                <li>
                  <Link to="/#divisions" className="hover:text-orange-400 transition-colors">
                    Revamping
                  </Link>
                </li>
                <li>
                  <Link to="/#divisions" className="hover:text-orange-400 transition-colors">
                    Costruzioni
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-lg">Azienda</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>
                  <Link to="/#about" className="hover:text-orange-400 transition-colors">
                    Chi Siamo
                  </Link>
                </li>
                <li>
                  <Link to="/#projects" className="hover:text-orange-400 transition-colors">
                    Portfolio
                  </Link>
                </li>
                <li>
                  <Link to="/codice-etico" className="hover:text-orange-400 transition-colors">
                    Codice Etico
                  </Link>
                </li>
                <li>
                  <Link to="/#contact" className="hover:text-orange-400 transition-colors">
                    Contatti
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-lg">Contatti</h4>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  {companyInfo.phone}
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {companyInfo.email}
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Cassino (FR)
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-400 text-sm">
                © {new Date().getFullYear()} {companyInfo.legalName} - P.IVA: {companyInfo.vat}
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default CaseStudyDetail;
